import { useMemo, useRef } from 'react';
import { useComparisonOptional } from '../../providers/ComparisonProvider';
import type { Result } from '../../types/result';
import { emitCompareAdded, emitCompareRemoved } from './compareAnalytics';

/**
 * Shape returned by {@link useComparisonForProgram}. Matches the
 * compare-related prop names on `@highereducation/hen-ui`
 * `DegreeCardDetailed` so callers can spread the result directly.
 */
export type ComparisonProps = {
  compare: boolean;
  compareChecked: boolean;
  compareDisabled: boolean;
  onCompareChange: ((checked: boolean) => void) | undefined;
};

/**
 * Eventing context the surrounding card holds but the `Result` does
 * not — the card's 1-based `position` and the results-page
 * `viewCorrelationId`. Threaded onto the compare add/remove beams so
 * the `webElement.position` and top-level `viewCorrelationId` are
 * populated. Both optional: surfaces without a position (DSL
 * action-dispatch, recommender popups) simply omit them.
 */
export type ComparisonEventingContext = {
  position?: number;
  viewCorrelationId?: string;
  /**
   * Click correlation id for the beam's top-level `correlationId`. When
   * omitted the emitter mints a fresh uuid so the beam never carries an
   * undefined correlation. Supply the surrounding surface's id (the
   * card's `productClickedCorrelationId`, or the DSL flow's
   * `formViewedCorrelationId`) to keep the compare beam on the same
   * correlation chain as its neighbours.
   */
  correlationId?: string;
};

/**
 * Derives the compare-checkbox props for a single program by reading
 * the optional `ComparisonProvider` context. When no provider is in
 * the tree, or no concrete program was supplied, the hook returns a
 * no-op shape with `compare: false` so the surrounding card can render
 * as a non-compare card without crashing (used by Recommender /
 * Spotlight popups + any caller that hasn't mounted a
 * `ComparisonProvider`).
 *
 * When a provider IS present, the hook:
 * - reflects selection state via `isProgramSelected(programId)`
 * - disables the checkbox when at-cap AND this program is not in the set
 * - on toggle: add/remove via the provider's mutators AND emit the
 *   matching Tagular beam (see `./compareAnalytics`)
 *
 * The return value is memoized on the provider snapshot + the program
 * reference, so a stable program object will yield a referentially
 * stable result across renders.
 */
export function useComparisonForProgram(
  program?: Result,
  eventing?: ComparisonEventingContext,
): ComparisonProps {
  const comparison = useComparisonOptional();
  // `ComparisonProvider` does not currently memoize its context value, so
  // `comparison` is a fresh wrapper on every render even when underlying
  // state is unchanged. Depend on the narrower state slices that ARE
  // reference-stable across no-op renders (the programs array + max-cap
  // flag), and route the mutation calls through a ref so callbacks
  // always see the latest provider snapshot without invalidating the
  // memo. This gives consumers a stable return value when neither the
  // program nor the selection state has changed.
  const programsToCompare = comparison?.programsToCompare;
  const canAddMorePrograms = comparison?.canAddMorePrograms ?? true;
  // DSL result-step config id (present only on the DSL result page) so
  // the compare add/remove beams carry `dslConfigId` at parity with the
  // action-dispatch path.
  const dslConfigId = comparison?.dslConfigId;

  // Card-held eventing context the `Result` does not carry. Destructured
  // to primitives so the memo stays reference-stable when callers pass a
  // fresh `eventing` object literal each render.
  const position = eventing?.position;
  const viewCorrelationId = eventing?.viewCorrelationId;
  const correlationId = eventing?.correlationId;

  const comparisonRef = useRef(comparison);
  comparisonRef.current = comparison;

  return useMemo<ComparisonProps>(() => {
    if (!comparisonRef.current || !program) {
      return {
        compare: false,
        compareChecked: false,
        compareDisabled: false,
        onCompareChange: undefined,
      };
    }

    const selected =
      programsToCompare?.some((p) => p.programId === program.programId) ??
      false;
    const disabled = !canAddMorePrograms && !selected;

    const onCompareChange = (next: boolean): void => {
      const ctx = comparisonRef.current;
      if (!ctx) return;
      const analytics = {
        schoolSlug: program.schoolSlug,
        position,
        viewCorrelationId,
        correlationId: correlationId ?? program.productClickedCorrelationId,
      };
      if (next) {
        const added = ctx.addProgramToCompare(program);
        if (added) {
          emitCompareAdded(analytics, dslConfigId);
        }
        return;
      }
      // Mirror the add-side guard: only emit when the toggle changes state.
      // `removeProgramFromCompare` silently no-ops on a non-selected id, so a
      // programmatic or stale-state deselect would otherwise emit a phantom
      // CompareRemoved beam.
      if (!ctx.isProgramSelected(program.programId)) return;
      ctx.removeProgramFromCompare(program.programId);
      emitCompareRemoved(analytics, dslConfigId);
    };

    return {
      compare: true,
      compareChecked: selected,
      compareDisabled: disabled,
      onCompareChange,
    };
  }, [
    program,
    programsToCompare,
    canAddMorePrograms,
    dslConfigId,
    position,
    viewCorrelationId,
    correlationId,
  ]);
}
